import {Howl, Howler} from "howler";
import * as PIXI from "pixi.js";
import {config} from "../config";
import {SessionTracker} from "../SessionTracker";
import {ApiService} from "./api/ApiService";
import {CardColor} from "./api/CardColor";
import {ServerResponse} from "./api/serverResponseClasses/ServerResponse";
import {VideoPokerHand} from "./api/serverResponseClasses/VideoPokerHand";
import {VideoPokerInfoForClient} from "./api/serverResponseClasses/VideoPokerInfoForClient";
import {VideoPokerAction} from "./api/VideoPokerAction";
import {VideoPokerActionName} from "./api/VideoPokerActionName";
import {Card} from "./Card";
import {CardCombinationDetector} from "./CardCombinationDetector";
import {CardsSimulation} from "./CardsSimulation";
import data from "./configs/data";
import {DoubleUpGame} from "./DoubleUpGame";
import {FunModeFeatures} from "./FunModeFeatures/FunModeFeatures";
import {HowToPlay} from "./HowToPlay";
import {InfoModal} from "./InfoModal";
import {PayTable} from "./PayTable";
import {SettingsMenu} from "./SettingsMenu";
import {GamePanel} from "./userInterface/GamePanel";
import {Utils} from "./Utils";
import {VideoPokerCardCombinationName} from "./VideoPokerCardCombinationName";
import {WinPopupAnimation} from "./WinPopupAnimation";

export class VideoPoker extends PIXI.Container {

    private readonly HOW_TO_PLAY_POSITION = new PIXI.Point(0, 0);
    private readonly GAME_PANEL_POSITION = new PIXI.Point(0, 572);
    private readonly PAY_TABLE_POSITION = new PIXI.Point(493, 154);
    private readonly SETTINGS_MENU_POSITION = new PIXI.Point(236, 561);
    private readonly WIN_POPUP_ANIMATION_POSITION = new PIXI.Point(640, 495);

    private steemAccountName: string;
    private steemConnectToken: string;
    private keychainJWT: string;

    private apiService = new ApiService();
    private sessionTracker: SessionTracker;
    private connectionTimeOut: any;
    private showSlowConnectionModalAgain = true;
    private previousVideoPokerInfoForClient: VideoPokerInfoForClient;
    private cardsSimulation: CardsSimulation;
    private doubleUpGame: DoubleUpGame;
    private payTable: PayTable;

    private winPopupAnimation: WinPopupAnimation;

    private doubleUpWinSound: Howl;
    private regularWinSound: Howl;

    constructor() {
        super();

        this.createAndAddBackground();
        this.createAndAddPayTable();
        this.createAndAddCardsSimulation();
        this.createAndAddWinPopupAnimation();
        this.createAndAddGamePanel();
        this.createAndAddSettingsMenu();
        this.createAndAddHowToPlay();
        this.createAndAddDoubleUpGame();
        GamePanel.getInstance().disablePanel();

        this.doubleUpWinSound = new Howl({
            src: [data.audios.winDoubleUp],
            volume: 0.7,
        });
        this.regularWinSound = new Howl({
            src: [data.audios.regularWin],
            volume: 0.7,
        });
    }

    public setCredentials(steemAccountName: string, steemConnectToken: string, keychainJWT: string): void {
        this.steemAccountName = steemAccountName;
        this.steemConnectToken = steemConnectToken;
        this.keychainJWT = keychainJWT;
    }

    public async initGame(): Promise<any> {
        const serverResponse = (await this.apiService.initGame(
            this.steemAccountName, this.steemConnectToken, this.keychainJWT));
        this.sessionTracker = new SessionTracker(serverResponse.sessionParam);
        GamePanel.getInstance().updateBalance(serverResponse.accountInfo.steemAmount,
            serverResponse.accountInfo.sbdAmount);
        this.setGamePanelButtonStates(serverResponse.videoPokerInfoForClient);
        this.setCardsSimulationButtonStates(serverResponse.videoPokerInfoForClient);
        if (serverResponse.videoPokerInfoForClient) {
            this.cardsSimulation.showCards(serverResponse.videoPokerInfoForClient.videoPokerHand, true);
            this.doubleUpGame.setCurrentPotentialWinValue(
                serverResponse.videoPokerInfoForClient.totalPotentialWin, config.baseCurrency);
        }
        if (serverResponse.videoPokerInfoForClient &&
            serverResponse.videoPokerInfoForClient.changeDone &&
            serverResponse.videoPokerInfoForClient.doubleUpCards.length !== 0) {
            this.doubleUpGame.open(this.getCombinationName(serverResponse.videoPokerInfoForClient.videoPokerHand));
            for (let i = 0; i < serverResponse.videoPokerInfoForClient.doubleUpCards.length; i++) {
                await this.doubleUpGame.showCard(serverResponse.videoPokerInfoForClient.doubleUpCards[i], true);
            }
        }

        if (serverResponse.videoPokerInfoForClient &&
            !serverResponse.videoPokerInfoForClient.changeDone) {
            this.cardsSimulation.lockCards(
                this.getCombinationPositions(serverResponse.videoPokerInfoForClient.videoPokerHand));
        }

        if (serverResponse.videoPokerInfoForClient &&
            serverResponse.videoPokerInfoForClient.changeDone) {
            this.winPopupAnimation.visible = true;
            this.winPopupAnimation.showWinBanner(
                this.getCombinationName(serverResponse.videoPokerInfoForClient.videoPokerHand),
                serverResponse.videoPokerInfoForClient.totalPotentialWin, config.baseCurrency);
            this.payTable.showDot(this.getCombinationName(serverResponse.videoPokerInfoForClient.videoPokerHand));
        }

        this.previousVideoPokerInfoForClient = serverResponse.videoPokerInfoForClient;
    }

    private async setGameState(videoPokerAction: VideoPokerAction, hideBanner: boolean = true): Promise<void> {
        const gamePanel = GamePanel.getInstance();
        if (this.showSlowConnectionModalAgain) {
            this.startSlowConnectionCountDownAndInitializeBehavior();
        }

        gamePanel.disableCentralButton();
        gamePanel.disableBetPanel();
        gamePanel.disableDoubleUpButton();
        this.doubleUpGame.disableButtons();
        this.doubleUpGame.hideHighlighter();

        if (hideBanner) {
            this.winPopupAnimation.visible = false;
            this.payTable.hideDot();
        }

        if (videoPokerAction.getActionName() === VideoPokerActionName.DEAL) {
            if (this.previousVideoPokerInfoForClient) {
                this.cardsSimulation.hideCards();
            }
        }

        if (videoPokerAction.getActionName() === VideoPokerActionName.CHANGE) {
            this.cardsSimulation.hideLockerSymbols();
            await this.cardsSimulation.hideNotLockedCards();
            this.cardsSimulation.unlockAllCards();
        }

        const serverResponse: ServerResponse =
            await this.apiService.setGameState(videoPokerAction, config.baseCurrency);
        if (this.showSlowConnectionModalAgain) {
            this.removeConnectionCountDown();
        }

        if (videoPokerAction.getActionName() === VideoPokerActionName.DEAL) {
            await this.cardsSimulation.showCards(serverResponse.videoPokerInfoForClient.videoPokerHand,
                gamePanel.isSpeedIncreased());

            this.cardsSimulation.lockCards(
                this.getCombinationPositions(serverResponse.videoPokerInfoForClient.videoPokerHand));
        }

        if (videoPokerAction.getActionName() === VideoPokerActionName.CHANGE) {
            await this.cardsSimulation.changeCards(serverResponse.videoPokerInfoForClient.videoPokerHand,
                gamePanel.isSpeedIncreased());
            this.doubleUpGame.setCurrentPotentialWinValue(
                serverResponse.videoPokerInfoForClient.totalPotentialWin, config.baseCurrency);

            if (serverResponse.videoPokerInfoForClient.totalPotentialWin !== 0) {
                this.regularWinSound.play();
                this.winPopupAnimation.visible = true;
                this.winPopupAnimation.showWinBanner(
                    this.getCombinationName(serverResponse.videoPokerInfoForClient.videoPokerHand),
                    serverResponse.videoPokerInfoForClient.totalPotentialWin, config.baseCurrency);
                this.payTable.showDot(this.getCombinationName(serverResponse.videoPokerInfoForClient.videoPokerHand));
            }

            if (serverResponse.videoPokerInfoForClient.totalPotentialWin !== 0 &&
                SettingsMenu.getInstance().isAlwaysDoubleUpChecked()) {
                this.doubleUpGame.open(this.getCombinationName(serverResponse.videoPokerInfoForClient.videoPokerHand));
            }
        }

        if (videoPokerAction.getActionName() === VideoPokerActionName.DOUBLE) {
            const doubleUpCards = serverResponse.videoPokerInfoForClient.doubleUpCards;
            const lastCard: Card = doubleUpCards[doubleUpCards.length - 1];
            const isRedGuess = videoPokerAction.getIsRedGuess();
            await this.doubleUpGame.showCard(doubleUpCards[doubleUpCards.length - 1]);
            this.doubleUpGame.setCurrentPotentialWinValue(
                serverResponse.videoPokerInfoForClient.totalPotentialWin, config.baseCurrency);
            if (serverResponse.videoPokerInfoForClient.playerActionsFinished) {
                this.doubleUpGame.setCurrentPotentialWinValue(
                    serverResponse.videoPokerInfoForClient.totalWin, config.baseCurrency);
                this.doubleUpGame.hidePotentialWinValue();
            }

            if ((isRedGuess && (lastCard.getColor() === CardColor.DIAMOND || lastCard.getColor() === CardColor.HEART))
                ||
                (!isRedGuess && (lastCard.getColor() === CardColor.SPADE || lastCard.getColor() === CardColor.CLUB))) {
                this.doubleUpWinSound.play();
                if (serverResponse.videoPokerInfoForClient.playerActionsFinished) {
                    this.doubleUpGame.stopCardPulsing();
                    await Utils.requestTimeout(1500);
                    gamePanel.disableDoubleUpButton();
                    this.doubleUpGame.close();
                }
            } else {
                this.doubleUpGame.stopCardPulsing();
                await Utils.requestTimeout(1500);
                gamePanel.disableDoubleUpButton();
                this.doubleUpGame.close();
            }
        }

        this.setGamePanelButtonStates(serverResponse.videoPokerInfoForClient);
        this.setCardsSimulationButtonStates(serverResponse.videoPokerInfoForClient);
        this.doubleUpGame.enableButtons();
        this.doubleUpGame.showHighlighter();

        this.sessionTracker.reloadIfSessionParamMismatch(serverResponse.sessionParam);
        gamePanel.updateBalance(serverResponse.accountInfo.steemAmount,
            serverResponse.accountInfo.sbdAmount);
        gamePanel.updateWinValue(serverResponse.videoPokerInfoForClient.totalWin);
        gamePanel.enableCentralButton();

        if (SettingsMenu.getInstance().isNeverDoubleUpChecked() &&
            serverResponse.videoPokerInfoForClient.totalPotentialWin !== 0 &&
            videoPokerAction.getActionName() === VideoPokerActionName.CHANGE) {
            await this.setGameState(new VideoPokerAction(VideoPokerActionName.PAYOUT), false);
            return;
        }

        FunModeFeatures.addSpin();
        this.previousVideoPokerInfoForClient = serverResponse.videoPokerInfoForClient;
    }

    private startSlowConnectionCountDownAndInitializeBehavior(): void {
        this.connectionTimeOut = setTimeout(() => {
            InfoModal.open("Internet connection problems.",
                "You lost you internet connection or your connection is slow.", () => {
                    this.showSlowConnectionModalAgain = false;
                }, true);
            InfoModal.setActionButtonText("Don't show again.");
        }, 5000);
    }

    private setGamePanelButtonStates(videoPokerInfoForClient: VideoPokerInfoForClient): void {
        GamePanel.getInstance().enableCentralButton();

        if (videoPokerInfoForClient && !videoPokerInfoForClient.playerActionsFinished) {
            GamePanel.getInstance().disableBetPanel();
            GamePanel.getInstance().disableCurrencySwitcher();
        }

        if (!videoPokerInfoForClient || videoPokerInfoForClient.playerActionsFinished) {
            GamePanel.getInstance().showDealButton();
            GamePanel.getInstance().enableBetPanel();
            GamePanel.getInstance().enableCurrencySwitcher();
        }

        if (videoPokerInfoForClient && !videoPokerInfoForClient.changeDone) {
            GamePanel.getInstance().disableDoubleUpButton();
            GamePanel.getInstance().showDrawButton();
        }

        if (videoPokerInfoForClient &&
            videoPokerInfoForClient.changeDone &&
            !videoPokerInfoForClient.playerActionsFinished) {
            GamePanel.getInstance().showCollectButton();
            GamePanel.getInstance().enableDoubleUpButton();
        }
    }

    private setCardsSimulationButtonStates(videoPokerInfoForClient: VideoPokerInfoForClient): void {
        if (videoPokerInfoForClient &&
            !videoPokerInfoForClient.changeDone &&
            !videoPokerInfoForClient.playerActionsFinished) {
            this.cardsSimulation.enableButtons();
        } else {
            this.cardsSimulation.disableButtons();
        }
    }

    private removeConnectionCountDown(): void {
        clearTimeout(this.connectionTimeOut);
    }

    private createAndAddPayTable(): void {
        this.payTable = new PayTable();
        this.payTable.position = this.PAY_TABLE_POSITION;
        this.payTable.hideDot();
        this.addChild(this.payTable);
    }

    private createAndAddBackground(): void {
        const background: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.gameBackground);
        this.addChild(background);
    }

    private createAndAddSettingsMenu(): void {
        const settingsMenu: SettingsMenu = SettingsMenu.getInstance();
        settingsMenu.position = this.SETTINGS_MENU_POSITION;
        this.addChild(settingsMenu);
    }

    private createAndAddGamePanel(): void {
        const gamePanel = GamePanel.getInstance();
        gamePanel.position = this.GAME_PANEL_POSITION;
        gamePanel.addOnDealButtonClickedListener(() => {
            const videoPokerAction =
                new VideoPokerAction(VideoPokerActionName.DEAL, gamePanel.getBetValue());
            this.setGameState(videoPokerAction);
        });
        gamePanel.addOnDrawButtonClickedListener(() => {
            const videoPokerAction = new VideoPokerAction(VideoPokerActionName.CHANGE,
                null, this.cardsSimulation.getNotLockedCardsIndexes());
            this.setGameState(videoPokerAction);
        });
        gamePanel.addOnCollectButtonClickedListener(() => {
            const videoPokerAction = new VideoPokerAction(VideoPokerActionName.PAYOUT);
            this.setGameState(videoPokerAction);
        });
        gamePanel.addOnDoubleUpButtonClickedListener(() => {
            this.doubleUpGame.open(this.getCombinationName(this.previousVideoPokerInfoForClient.videoPokerHand));
        });
        gamePanel.addOnSpeedChangedListener((checked: boolean) => {
            if (checked) {
                SettingsMenu.getInstance().checkSpeedCheckbox();
            } else {
                SettingsMenu.getInstance().uncheckSpeedCheckbox();
            }
        });
        this.addChild(gamePanel);
    }

    private createAndAddDoubleUpGame(): void {
        this.doubleUpGame = new DoubleUpGame();
        this.doubleUpGame.close();
        this.doubleUpGame.addRedButtonListener(() => {
            const videoPokerAction = new VideoPokerAction(
                VideoPokerActionName.DOUBLE, null, null, true);
            this.setGameState(videoPokerAction);
        });
        this.doubleUpGame.addBlackButtonListener(() => {
            const videoPokerAction = new VideoPokerAction(
                VideoPokerActionName.DOUBLE, null, null, false);
            this.setGameState(videoPokerAction);
        });
        this.doubleUpGame.addCollectButtonListener(async () => {
            const videoPokerAction = new VideoPokerAction(
                VideoPokerActionName.PAYOUT);
            this.doubleUpGame.close();
            this.winPopupAnimation.visible = false;
            this.payTable.hideDot();
            GamePanel.getInstance().disableDoubleUpButton();
            await this.setGameState(videoPokerAction);
        });
        this.addChild(this.doubleUpGame);
    }

    private getCombinationName(videoPokerHand: VideoPokerHand): VideoPokerCardCombinationName {
        return CardCombinationDetector.detect(videoPokerHand.getCards()).getName();
    }

    private getCombinationPositions(videoPokerHand: VideoPokerHand): Array<number> {
        return CardCombinationDetector.detect(videoPokerHand.getCards()).getPositions();
    }

    private createAndAddCardsSimulation(): void {
        this.cardsSimulation = new CardsSimulation();
        this.addChild(this.cardsSimulation);
    }

    private createAndAddWinPopupAnimation(): void {
        this.winPopupAnimation = new WinPopupAnimation();
        this.winPopupAnimation.position = this.WIN_POPUP_ANIMATION_POSITION;
        (window as any).winPopupAnimation = this.winPopupAnimation;
        this.addChild(this.winPopupAnimation);
    }

    private createAndAddHowToPlay(): void {
        HowToPlay.getInstance().position = this.HOW_TO_PLAY_POSITION;
        this.addChild(HowToPlay.getInstance());
    }
}



// WEBPACK FOOTER //
// ./src/app/VideoPoker.ts